import {
	ClientConstructionSite,
	ClientContract,
	ClientProposition,
	ClientReceipt,
	ClientsCountResult,
	GetClientReceiptsParams,
	GetClientsQueryParams,
	PaginatedResponse,
} from "@dune/types";
import { ClientDetailsWithConstructionSites } from "@dune/vars";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { privateAxios } from "../axios";
import { clientsQueryKeys } from "../query-keys/clients.query-keys";

const getClients = async (
	search?: string,
	status?: string,
	type?: string,
	page?: number,
	limit?: number,
) => {
	const searchParams = new URLSearchParams();
	if (search) searchParams.append("search", search);
	if (status) searchParams.append("status", status);
	if (type) searchParams.append("type", type);
	if (page) searchParams.append("page", String(page));
	if (limit) searchParams.append("limit", String(limit));

	const { data } = await privateAxios.get<
		PaginatedResponse<ClientDetailsWithConstructionSites>
	>(`/clients?${searchParams.toString()}`);
	return data;
};

const getClientById = async (clientId: string, clientType?: string) => {
	const searchParams = new URLSearchParams();
	if (clientType) {
		searchParams.append("clientType", clientType);
	}
	const queryString = searchParams.toString();
	const url = `/clients/${clientId}${queryString ? `?${queryString}` : ""}`;
	const { data } =
		await privateAxios.get<ClientDetailsWithConstructionSites>(url);
	return data;
};

const getClientConstructionSites = async (
	clientId: string,
	clientType?: string,
): Promise<ClientConstructionSite[]> => {
	const searchParams = new URLSearchParams();
	if (clientType) {
		searchParams.append("clientType", clientType);
	}
	const queryString = searchParams.toString();
	const url = `/clients/${clientId}/construction-sites${queryString ? `?${queryString}` : ""}`;
	const { data } = await privateAxios.get<ClientConstructionSite[]>(url);
	return data;
};

const getClientPropositions = async (
	clientId: string,
	clientType?: string,
): Promise<PaginatedResponse<ClientProposition>> => {
	const searchParams = new URLSearchParams();
	if (clientType) {
		searchParams.append("clientType", clientType);
	}
	const queryString = searchParams.toString();
	const url = `/clients/${clientId}/propositions${queryString ? `?${queryString}` : ""}`;
	const { data } =
		await privateAxios.get<PaginatedResponse<ClientProposition>>(url);
	return data;
};

const getClientContracts = async (
	clientId: string,
	clientType?: string,
): Promise<PaginatedResponse<ClientContract>> => {
	const searchParams = new URLSearchParams();
	if (clientType) {
		searchParams.append("clientType", clientType);
	}
	const queryString = searchParams.toString();
	const url = `/clients/${clientId}/contracts${queryString ? `?${queryString}` : ""}`;
	const { data } =
		await privateAxios.get<PaginatedResponse<ClientContract>>(url);
	return data;
};

const getClientReceipts = async (
	clientId: string,
	params?: GetClientReceiptsParams,
	clientType?: string,
): Promise<ClientReceipt[]> => {
	const searchParams = new URLSearchParams();
	if (clientType) {
		searchParams.append("clientType", clientType);
	}
	if (params) {
		for (const [key, value] of Object.entries(params)) {
			if (value !== undefined) {
				searchParams.append(key, String(value));
			}
		}
	}
	const queryString = searchParams.toString();
	const url = `/clients/${clientId}/receipts${queryString ? `?${queryString}` : ""}`;
	const { data } = await privateAxios.get<ClientReceipt[]>(url);
	return data;
};

const getClientsCounts = async (
	params: GetClientsQueryParams,
): Promise<ClientsCountResult> => {
	const searchParams = new URLSearchParams();
	if (params.search) searchParams.append("search", params.search);
	if (params.type) searchParams.append("type", params.type);

	const { data } = await privateAxios.get<ClientsCountResult>(
		`/clients/counts?${searchParams.toString()}`,
	);
	return data;
};

export const useGetClientsList = ({
	limit,
	page,
	search,
	status,
	type,
}: GetClientsQueryParams) => {
	return useQuery({
		queryKey: clientsQueryKeys.list(search, status, type, page, limit),
		queryFn: () => getClients(search, status, type, page, limit),
		placeholderData: keepPreviousData,
	});
};

export const useGetClientById = (clientId: string, clientType?: string) => {
	return useQuery({
		queryKey: clientsQueryKeys.detail(clientId, clientType),
		queryFn: () => getClientById(clientId, clientType),
		enabled: !!clientId,
	});
};

export const useGetClientConstructionSites = (
	clientId: string,
	clientType?: string,
) => {
	return useQuery({
		queryKey: clientsQueryKeys.constructionSites(clientId, clientType),
		queryFn: () => getClientConstructionSites(clientId, clientType),
		enabled: !!clientId,
	});
};

export const useGetClientPropositions = (
	clientId: string,
	clientType?: string,
) => {
	return useQuery({
		queryKey: clientsQueryKeys.propositions(clientId, undefined, clientType),
		queryFn: () => getClientPropositions(clientId, clientType),
		enabled: !!clientId,
	});
};

export const useGetClientContracts = (
	clientId: string,
	clientType?: string,
) => {
	return useQuery({
		queryKey: clientsQueryKeys.contracts(clientId, undefined, clientType),
		queryFn: () => getClientContracts(clientId, clientType),
		enabled: !!clientId,
	});
};

export const useGetClientReceipts = (
	clientId: string,
	params?: GetClientReceiptsParams,
	clientType?: string,
) => {
	return useQuery({
		queryKey: clientsQueryKeys.receipts(clientId, params, clientType),
		queryFn: () => getClientReceipts(clientId, params, clientType),
		enabled: !!clientId,
	});
};

export const useGetClientsCounts = (params: GetClientsQueryParams) => {
	return useQuery({
		queryKey: clientsQueryKeys.counts(params.search, params.type),
		queryFn: () => getClientsCounts(params),
	});
};
